/** @jsx jsx */
import { useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import { Box, xcss, Text, Pressable } from '@atlaskit/primitives';
import { IconButton } from '@atlaskit/button/new';
import ChevronLeftIcon from '@atlaskit/icon/utility/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { token } from '@atlaskit/tokens';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import { useScrollCarousel } from '@atlassian/jira-canned-comments/src/controllers/use-scroll-carousel/index.tsx';
import { useDataForRecommendationsInEditor } from './data-for-recommendations-in-editor.tsx';

type EditorMentionsAPI = PublicPluginAPI<[MentionsPlugin]>;

export const MentionsContainer = ({ api }: { api?: EditorMentionsAPI }) => {
	const {
		setRef,
		setCanScrollDebounced,
		shouldHideScrollLeftButton,
		shouldHideScrollRightButton,
		scrollToNext,
		scrollToPrevious,
	} = useScrollCarousel();

	const mentionUsersData = useDataForRecommendationsInEditor();

	const handleMentionClick = useCallback(
		(name: string, userId: string) => {
			api?.core.actions.execute(({ tr }) => {
				const { schema } = tr.doc.type;
				const mentionNode = schema.nodes.mention.createChecked(
					{
						text: name,
						id: userId,
					},
					null,
					undefined,
				);
				return tr.insert(tr.selection.from, mentionNode);
			});
		},
		[api],
	);

	return (
		<Box xcss={boxStyles}>
			<Box
				xcss={[hoverButtonStyle, leftButtonStyle, shouldHideScrollLeftButton && hiddenButtonStyle]}
			>
				<IconButton
					shape="circle"
					appearance="subtle"
					icon={ChevronLeftIcon}
					isTooltipDisabled
					label="Previous"
					onClick={scrollToPrevious}
					tabIndex={-1}
				/>
			</Box>
			<div
				ref={setRef}
				css={commentsContainerStyle}
				onScroll={setCanScrollDebounced}
				role="group"
				aria-label="Mention"
			>
				<Text>Add mentions:</Text>
				{mentionUsersData.map((user) => (
					<Pressable
						key={user.userId}
						onClick={() => handleMentionClick(user.name, user.userId)}
						xcss={buttonStyles}
					>
						{`+ @${user.name}`}
					</Pressable>
				))}
			</div>
			<Box
				xcss={[
					hoverButtonStyle,
					rightButtonStyle,
					shouldHideScrollRightButton && hiddenButtonStyle,
				]}
			>
				<IconButton
					shape="circle"
					appearance="default"
					icon={ChevronRightIcon}
					isTooltipDisabled
					label="Next"
					onClick={scrollToNext}
					tabIndex={-1}
				/>
			</Box>
		</Box>
	);
};

const boxStyles = xcss({
	padding: 'space.100',
	backgroundColor: 'color.background.neutral',
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
	overflowX: 'auto',
	whiteSpace: 'nowrap',
	position: 'relative',
});

const buttonStyles = xcss({
	height: 'space.250',
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	borderRadius: '3px',
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderStyle: 'solid',
});

const commentsContainerStyle = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: token('space.100'),
	paddingTop: token('space.075'),
	paddingRight: token('space.075'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.075'),

	overflowX: 'scroll',
	scrollbarWidth: 'none',
	'-ms-overflow-style': 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'::-webkit-scrollbar': {
		display: 'none',
	},
});

const hoverButtonStyle = xcss({
	zIndex: 'layer',
	alignItems: 'center',
	position: 'absolute',
	background: `var(--jira-canned-comments-background-color, ${token('color.background.neutral')})`,
	opacity: 1,
});

const rightButtonStyle = xcss({
	right: 'space.0',
});

const leftButtonStyle = xcss({
	left: 'space.0',
});

const hiddenButtonStyle = xcss({
	display: 'none',
});
